import { User, UserManager, WebStorageStateStore, type OidcClientSettings } from 'oidc-client-ts'
import { inited, authed, getFrameworkVar, getFrameworkServiceUrl, getOidcClientId } from './states'
import pLimit from 'p-limit'
import fetch, { getUrlObject } from '../libs/fetch'
import {
  INSTANCE_STORE_URL_KEY,
  TENANCY_SERVICE_URL_KEY,
  APP_REGISTRY_URL_KEY,
  TSG_ID_QUERY_NAMES,
  TSG_ID_NONE,
  OIDC_STORE_PREFIX,
  TSG_ID_REGEX,
  API_GW_GLOBAL_SVC_URL_KEY,
  PROP_TSG_INSTANCES,
  PROP_NETSEC_FEATURES,
} from './constants'
import { tryParseJson } from '../init'
import { dedupPromise, deepFreeze, isEmptyObject, isPlainObject, isString, jwtDecode, tryParseErrorJson } from './utils'
import { indexTsgTree, indexTsgInstances, indexAppManifests, indexFeatureFlags } from './indexedArrays'
import history from './history'
import {
  parseAccess,
  fetchIamRolePermissionDefinition,
  fetchIamCustomRoleDefinitionByJwt,
  fetchIamScopeDefinitionByJwt,
  indexScopes,
  indexCustomRoles,
} from './access'
import { overrideTsgInstances } from './overrideInstances'
import { DEFAULT_OIDC_CONFIG, getOidcUrl } from './oidcConfig'
import { getDatadogRum } from './externals'
import { __error, __isDebugOn } from '../init/debug'
import type { Instance, Tsg, ReadonlyTsgTree, ReadonlyTsgInstances, TsgInstanceError } from './indexedArrays'
import type {
  AppManifests,
  IamCustomRolesDefinition,
  IamJwtPayload,
  IamRolePermissionDefinition,
  IamScopesDefinition,
  OidcClientConfig,
  ReadonlyAccess,
  ReadonlyFeatureFlags,
  ReadonlyTsgProperties,
  TsgProperties,
} from './stateTypes'

const TSG_LOGIN_POOL_SIZE = 5
const TSG_TOKEN_POOL_SIZE = 10
const FETCH_CACHING_DURATION = 5000 // 5s
const MIN_TSG_JWT_EXP = 30 // s
const MAX_TSG_JWT_EXP = 28800000 // 8hr

export type TsgIdAwareType = {
  tsg_id?: string
}

export type TsgUserManager = UserManager & TsgIdAwareType
export type TsgUser = User
export type { Instance, Tsg, ReadonlyTsgTree, ReadonlyTsgInstances }

export type TsgUserManagers = Record<string, TsgUserManager> & {
  __current_tsg_id__?: string
  __current_user_id__?: string
  __metadata_promise__?: Promise<OidcClientSettings['metadata']>
  getCurrentTsgId: () => string | undefined
}
export type JwtError = Error & { tsg_id: string, access_token: string, access_token_decoded: TsgIdAwareType, user: TsgUser }

export { User, indexTsgTree, indexTsgInstances, indexCustomRoles, TSG_ID_NONE }

export const hasTsgSupport = (): boolean => {
  return Boolean(getOidcClientId())
}

export const tsgUserManagers = Object.defineProperties({}, {
  getCurrentTsgId: {
    writable: true,
    value: () => tsgUserManagers.__current_tsg_id__
  },
}) as TsgUserManagers

class InMemoryWebStorage implements Storage {
  private _data: Record<string, string> = {}

  public clear(): void {
    this._data = {}
  }

  public getItem(key: string): string {
    return this._data[key]
  }

  public setItem(key: string, value: string): void {
    this._data[key] = value
  }

  public removeItem(key: string): void {
    delete this._data[key]
  }

  public get length(): number {
    return Object.getOwnPropertyNames(this._data).length
  }

  public key(index: number): string {
    return Object.getOwnPropertyNames(this._data)[index]
  }
}

type UserManagerWithToken = [TsgUserManager, string]
const getDefaultTsgUserManagerWithToken = async (): Promise<UserManagerWithToken> => {
  if (getFrameworkVar('use_provided_tsg_token')) {
    const userMgr = tsgUserManagers.none
    if (userMgr) {
      const user = await userMgr.getUser()
      if (user?.access_token) {
        return [
          userMgr,
          user.access_token,
        ]
      }
    }
    throw new Error('default tsg user manager is not found for use_provided_tsg_token mode')
  }
  const { config_url, authority = config_url, ...settings } = getFrameworkVar<OidcClientConfig>('oidc_client') || {} as OidcClientConfig
  const client_id = getOidcClientId()
  if (!client_id || !authority) {
    throw new Error('Missing oidc config')
  }
  const metadata = await tsgUserManagers.__metadata_promise__
  const store = new WebStorageStateStore({ prefix: OIDC_STORE_PREFIX, store: new InMemoryWebStorage() })
  const silent_redirect_uri = getOidcUrl('silent_redirect_uri', settings)
  const defaultTsgUserManager = new UserManager({
    ...DEFAULT_OIDC_CONFIG,
    ...settings,
    redirect_uri: silent_redirect_uri,
    silent_redirect_uri,
    authority: authority || '',
    client_id,
    loadUserInfo: false,
    automaticSilentRenew: false,
    scope: 'profile email',
    metadata,
    stateStore: store,
    userStore: store,
  }) as TsgUserManager

  await defaultTsgUserManager.signinSilent()
  const user = await defaultTsgUserManager.getUser()
  if (!user?.scope?.includes('tsg_id:')) {
    throw new Error('Invalid access token returned')
  }
  const tsg_id = user.scope.match(/tsg_id:(\d+|none)\b/)?.[1]
  const tsgUserManager = getUserManager(tsg_id)
  if (tsg_id !== TSG_ID_NONE) {
    tsgUserManager.storeUser(user)
  }
  return [
    tsgUserManager,
    user.access_token,
  ]
}
export const tryGetUserManagerWithToken = async (tsg_id?: string): Promise<UserManagerWithToken> => {
  if (!tsg_id || tsg_id === TSG_ID_NONE) {
    return getDefaultTsgUserManagerWithToken()
  }
  await tsgUserManagers.__metadata_promise__
  const tsgUserManager = getUserManager(tsg_id)
  const promise = (tsgUserManager as {
    _get_user_promise?: Promise<TsgUser>
  })._get_user_promise || getUserFromManager(tsgUserManager)
  try {
    const user = await promise
    return [
      tsgUserManager,
      user.access_token,
    ]
  }
  catch (e) {
    __error('error getting user from TSG user manager', e)
    const { tsg_id: decodedTsgId, user } = (e as JwtError) || {}
    delete tsgUserManagers[tsg_id]
    if (user && decodedTsgId !== tsg_id) {
      const retried = getUserManager(decodedTsgId)
      if (retried.tsg_id !== TSG_ID_NONE) {
        retried.storeUser(user)
        Object.defineProperty(retried, 'org_tsg_id', { value: tsg_id, writable: true })
      }
      return [
        retried,
        user.access_token,
      ]
    }
    const results = await getDefaultTsgUserManagerWithToken()
    const mgr = results[0]
    if (mgr.tsg_id !== TSG_ID_NONE) {
      Object.defineProperty(mgr, 'org_tsg_id', { value: tsg_id, writable: true })
    }
    return results
  }
}

export const getUserManager = (tsg_id?: string) => {
  tsg_id = String(tsg_id)
  if (getFrameworkVar('use_provided_tsg_token')) {
    const userMgr = tsgUserManagers.none
    if (userMgr && (!tsg_id || tsg_id === TSG_ID_NONE || tsg_id === userMgr.tsg_id)) {
      return userMgr
    }
    throw new Error('default tsg user manager is not found for use_provided_tsg_token mode')
  }
  if (getFrameworkVar('private_site_origin') && !getFrameworkVar('private_site_default_tsg')) {
    const userMgr = tsgUserManagers.none
    if (userMgr) {
      return userMgr
    }
    throw new Error('default tsg user manager is not found for private site without tsg mode')
  }
  const { config_url, authority = config_url, ...settings } = getFrameworkVar<OidcClientConfig>('oidc_client') || {} as OidcClientConfig
  const client_id = getOidcClientId()
  if (!tsg_id || !client_id || !authority) {
    throw new Error('Missing tsg_id or oidc config')
  }
  if (tsg_id !== TSG_ID_NONE && !TSG_ID_REGEX.test(tsg_id)) {
    throw new Error('Invalid TSG ID format')
  }
  const existingMgr = tsgUserManagers[tsg_id]
  if (existingMgr) {
    return existingMgr
  }
  const metadata = tsgUserManagers.none?.settings?.metadata
  const store = new WebStorageStateStore({ prefix: `${OIDC_STORE_PREFIX}${tsg_id}.`, store: sessionStorage })
  const silent_redirect_uri = getOidcUrl('silent_redirect_uri', settings)
  const tsgUserManager = new UserManager({
    ...DEFAULT_OIDC_CONFIG,
    redirect_uri: silent_redirect_uri,
    silent_redirect_uri,
    ...settings,
    authority,
    client_id,
    loadUserInfo: false,
    scope: `openid profile email tsg_id:${tsg_id}`,
    metadata,
    stateStore: store,
    userStore: store,
  }) as TsgUserManager
  tsgUserManager.tsg_id = tsg_id
  tsgUserManagers[tsg_id] = tsgUserManager
  return tsgUserManager
}

const cachedParsedTsgTokens = new Map()
const MAX_PARSED_TSG_CACHE = 10
export const jwtDecodeCached = (access_token: string, tsg_id?: string): IamJwtPayload => {
  const cached = cachedParsedTsgTokens.get(tsg_id) // allow tsg_id is undefined
  if (cached?.parsed && cached.access_token === access_token && cached.parsed.tsg_id === tsg_id) {
    return cached.parsed as IamJwtPayload
  }
  const parsed = jwtDecode(access_token) as TsgIdAwareType
  if (tsg_id && tsg_id !== parsed.tsg_id) {
    return parsed as IamJwtPayload // skip cache if tsg_id is different
  }
  cachedParsedTsgTokens.set(parsed.tsg_id, { // can be undefined
    tsg_id,
    access_token,
    parsed,
  })
  if (cachedParsedTsgTokens.size > MAX_PARSED_TSG_CACHE) {
    const oldest = cachedParsedTsgTokens.keys().next().value
    cachedParsedTsgTokens.delete(oldest)
  }
  return parsed as IamJwtPayload
}

export type UserExpRefToken = { expires_at?: number, refresh_token?: string } | null | undefined
export const shouldDiscardUser = (user: UserExpRefToken, now: number = Date.now()): boolean => {
  if (!user) {
    return false
  }
  if (user?.expires_at && user.expires_at * 1000 < now - MAX_TSG_JWT_EXP) {
    return true
  }
  else if (user?.refresh_token) {
    const decoded = jwtDecode(user.refresh_token)
    return decoded.exp * 1000 < now
  }
  return false
}

export type GetUserFromManagerOpts = {
  tsg_id?: string | undefined
  forceSignIn?: boolean
}
export type LimitFn = (fn: () => Promise<TsgUser | null>) => Promise<TsgUser | null>
const globalLoginLimitFn = pLimit(TSG_LOGIN_POOL_SIZE) as LimitFn
const globalTokenLimitFn = pLimit(TSG_TOKEN_POOL_SIZE) as LimitFn
export const getUserFromManager = async (tsgUserManager: TsgUserManager, {
  tsg_id = tsgUserManager.tsg_id,
  forceSignIn,
}: GetUserFromManagerOpts = {}): Promise<TsgUser> => {
  if (!tsg_id) { // should not happen, just for typings
    throw new Error('tsg_id is missing')
  }
  const cachePromise = (promise?: Promise<User | null>) => {
    Object.defineProperties(tsgUserManager, {
      _get_user_promise: {
        configurable: true,
        value: promise
      }
    })
  }

  let tsgUser = await tsgUserManager.getUser()
  const isTsgUserMatched = () => tsgUser?.scopes.includes(`tsg_id:${tsg_id}`)
  if (tsgUser?.access_token && !tsgUser.expired && !forceSignIn &&
    tsgUser.expires_in! > MIN_TSG_JWT_EXP && isTsgUserMatched()) {
    return tsgUser
  }
  const existPromise = (tsgUserManager as { _get_user_promise?: Promise<TsgUser> })._get_user_promise
  if (existPromise) {
    return existPromise
  }
  // need renew or login again
  // if (tsg_id === TSG_ID_NONE) {
  //   throw new Error('No access token available, please sign-in again')
  // }
  if (shouldDiscardUser(tsgUser) || (tsgUser && !isTsgUserMatched())) { // discard expired saved user
    tsgUserManager.removeUser()
    tsgUser = null
  }
  const signin = (): Promise<User | null> => tsgUserManager.signinSilent().then((user) => {
    cachePromise()
    return user
  }, (err) => {
    if (tsgUser?.refresh_token) {
      console.warn('failed to silent login, try again', err)
      tsgUserManager.removeUser()
      return tsgUserManager.signinSilent().then((user) => {
        cachePromise()
        return user
      }, (err) => {
        cachePromise()
        throw err
      })
    }
    cachePromise()
    throw err
  })
  const limitFn = tsgUser?.refresh_token ? globalTokenLimitFn : globalLoginLimitFn
  const promise = limitFn(signin)
  cachePromise(promise)
  tsgUser = await promise
  if (!tsgUser?.access_token) {
    throw new Error('No access token returned')
  }
  const decoded = jwtDecodeCached(tsgUser.access_token, tsg_id) as IamJwtPayload
  if (!decoded.tsg_id || decoded.tsg_id !== tsg_id) {
    await tsgUserManager.removeUser()
    const error = new Error(decoded.tsg_id ? 'The tenant is not accessible' : 'Invalid access token returned') as JwtError
    error.access_token = tsgUser.access_token
    error.access_token_decoded = decoded
    error.user = tsgUser
    if (decoded.tsg_id) {
      error.tsg_id = decoded.tsg_id
    }
    console.error('getting tsg token does not return correct tsg_id', tsg_id, decoded)
    throw error
  }
  if (tsgUserManagers.__current_user_id__ &&
    tsgUserManagers.__current_user_id__ !== (decoded.sub || decoded.subname)) {
    await tsgUserManager.removeUser()
    const error = new Error('Incorrect access token returned') as JwtError
    error.access_token = tsgUser.access_token
    error.access_token_decoded = decoded
    error.user = tsgUser
    if (decoded.tsg_id) {
      error.tsg_id = decoded.tsg_id
    }
    console.error('getting tsg token does not return correct sub of the current user', tsg_id, decoded)
    throw error // TODO: consider prompt for re-login
  }
  return tsgUser
}

export const getTsgToken = async (tsg_id: string | undefined): Promise<string> => {
  if (!inited.result) {
    await inited // wait for init
  }
  const tsgUserManager = getUserManager(tsg_id)
  const promise = (tsgUserManager as any)._get_user_promise || ( // support private site without tsg
    !tsgUserManager.tsg_id ? tsgUserManager.getUser() : getUserFromManager(tsgUserManager)
  )
  const user = await promise
  if (!user?.access_token) {
    throw new Error('No access token available')
  }
  return user.access_token
}

export const revokeTsgToken = async (tsg_id: string | undefined): Promise<void> => {
  if (!inited.result) {
    await inited // wait for init
  }
  const tsgUserMgr = tsg_id ? tsgUserManagers[tsg_id] : undefined
  if (tsg_id && tsgUserMgr) {
    delete tsgUserManagers[tsg_id]
    await tsgUserMgr.revokeTokens()
    await tsgUserMgr.removeUser()
  }
}

export const revokeAllTsgTokens = async (): Promise<void> => {
  const tsgIds = Object.keys(tsgUserManagers).filter(tsgId =>
    tsgId && tsgId !== TSG_ID_NONE && !tsgId.startsWith('_')
  ) // the initial user manager removeUser will cause fail to logout
  await Promise.allSettled(tsgIds.map(tsgId => {
    const tsgUserMgr = tsgUserManagers[tsgId]
    const remove = () => {
      delete tsgUserManagers[tsgId]
      return tsgUserMgr.removeUser()
    }
    return tsgUserMgr?.revokeTokens().then(remove, remove)
  }))
  // ensure cleanup all keys except for default none
  const keys = Object.keys(sessionStorage).filter(key =>
    key.startsWith(OIDC_STORE_PREFIX) && key.slice(OIDC_STORE_PREFIX.length).split(':', 1)[0].includes('.')
  ) // ignore oidc.user or spk.oidc.user:
  for (const key of keys) {
    sessionStorage.removeItem(key)
  }
}

export const clearStoredExpiredTsgTokens = () => {
  const keys = Object.keys(sessionStorage).filter(key => key.startsWith(OIDC_STORE_PREFIX))
  const now = Date.now()
  for (const key of keys) {
    const parsed = tryParseJson(sessionStorage.getItem(key)) as UserExpRefToken
    if (shouldDiscardUser(parsed, now)) {
      sessionStorage.removeItem(key)
    }
  }
}

export const clearCurrentTsgId = () => {
  sessionStorage.removeItem('current_tsg_id')
  localStorage.removeItem('current_tsg_id')
  tsgUserManagers.__current_tsg_id__ = undefined
  if (window.name.startsWith('tsg:')) {
    window.name = ''
  }
}

export const setCurrentTsgId = (tsgId: string | undefined) => {
  if (!tsgId) {
    return clearCurrentTsgId()
  }
  tsgUserManagers.__current_tsg_id__ = tsgId
  if (tsgId !== TSG_ID_NONE) {
    sessionStorage.current_tsg_id = tsgId
    localStorage.current_tsg_id = tsgId
    window.name = `tsg:${tsgId}`
  }
}

const readTsgFromName = (): string | undefined => {
  const n = window.name
  if (/^tsg:1\d+$/.test(n)) {
    return n.slice(4)
  }
}
const convertStoredTsgIdValue = (tsgId: string | null | undefined): string | undefined => {
  return !tsgId || tsgId === TSG_ID_NONE ||
    tsgId === 'undefined' || tsgId === 'null' || tsgId === 'false' ||
    !TSG_ID_REGEX.test(tsgId) ? undefined : tsgId
}
const getTsgIdFromCurrentSession = (): string | undefined => {
  return readTsgFromName() || convertStoredTsgIdValue(sessionStorage.current_tsg_id)
}
const getTsgIdFromLocalStorage = () => {
  if (getFrameworkVar<boolean>('use_user_preferences')) {
    return undefined // ignore if user pref is enabled
  }
  return convertStoredTsgIdValue(localStorage.current_tsg_id)
}
export const getCurrentTsgId = (): string | undefined => {
  const tsgIdFromState = tsgUserManagers.getCurrentTsgId() ||
    tsgUserManagers.__current_tsg_id__ || undefined
  if (getFrameworkVar<boolean>('use_provided_tsg_token')) {
    return tsgIdFromState // ignore any store or name
  }
  const tsgId: string | undefined = tsgIdFromState ||
    getTsgIdFromCurrentSession() || getTsgIdFromLocalStorage()
  return tsgId
}

const TSG_ID_HASH_REGEX = new RegExp(`^#!(${TSG_ID_QUERY_NAMES.join('|')})=`)
export const getTsgIdFromUrl = ({ search, hash }: Location = window.location): string | undefined => {
  const urlParams = new URLSearchParams(search)
  const names = getFrameworkVar('private_site_default_tsg') ?
    ['tsgTenantId', ...TSG_ID_QUERY_NAMES] : TSG_ID_QUERY_NAMES
  for (const key of names) {
    const tsgId = urlParams.get(key)?.replace(/\W/g, '')
    if (tsgId && tsgId !== TSG_ID_NONE) {
      return tsgId
    }
  }
  const hashKey = (hash.startsWith('#!tsg') && hash.match(TSG_ID_HASH_REGEX)?.[1]) || ''
  if (hashKey) {
    const hashParams = new URLSearchParams(hash.slice(2))
    const tsgId = hashParams.get(hashKey)?.replace(/\W/g, '')
    if (tsgId && tsgId !== TSG_ID_NONE && TSG_ID_REGEX.test(tsgId)) {
      return tsgId
    }
  }
  return undefined
}

export const cleanupTsgIdFromUrl = ({ search, pathname }: Location = window.location) => {
  const urlParams = new URLSearchParams(search)
  for (const key of TSG_ID_QUERY_NAMES) {
    urlParams.delete(key)
  }
  history.replace({
    pathname,
    search: urlParams.toString(),
  }) // auto remove hash
}

export const getAndSaveInitTsgId = (): string | undefined => {
  const tsg_id = getTsgIdFromUrl()
  const curTsgId = getCurrentTsgId()
  if (tsg_id && curTsgId !== tsg_id && tsg_id !== TSG_ID_NONE) {
    setCurrentTsgId(tsg_id)
    return tsg_id
  }
  setCurrentTsgId(curTsgId) // ensure insync
  return curTsgId
}

export const getAuthHeaders = async (tsg_id: string | undefined, headers?: object) => {
  const jwt = await getTsgToken(tsg_id)
  return {
    ...headers,
    Authorization: `Bearer ${jwt}`
  } as Record<string, string>
}

export const getCurrentTsgToken = async () => {
  if (!authed.result) {
    await authed // wait for login
  } // wait for authed before get the tsg_id
  const tsgId = getCurrentTsgId()
  return getTsgToken(tsgId)
}
export const getCurrentAuthHeaders = (headers?: object) => getAuthHeaders(getCurrentTsgId(), headers)

const indexAndFilterTsgs = (items: Tsg[], jwt: string) => {
  if (getFrameworkVar('use_provided_tsg_token')) {
    const indexed = indexTsgTree(items)
    const decoded = jwtDecodeCached(jwt)
    const tsg = decoded?.tsg_id && decoded.tsg_id !== TSG_ID_NONE && indexed.get(decoded.tsg_id)
    if (tsg) {
      return indexTsgTree([{ ...tsg, children: undefined }])
    }
  }
  return indexTsgTree(items)
}
const loadTsgsCache = (email: string, url: string, jwt: string) => {
  if (sessionStorage.local_tsgs_cache) {
    const parsed = tryParseJson(sessionStorage.local_tsgs_cache)
    if (Array.isArray(parsed?.items) && parsed.items.length && parsed.email === email && parsed.url === url) {
      return indexAndFilterTsgs(parsed.items, jwt)
    }
  }
}
let tsgsPromise: Promise<ReadonlyTsgTree> | undefined
let initTsgLoading = true
export const fetchTsgs = async (jwt: string | undefined, email?: string): Promise<ReadonlyTsgTree> => {
  if (!jwt) {
    throw new Error('JWT token is required')
  }
  if (tsgsPromise) { // prevent multiple calls at the same time
    return tsgsPromise
  }
  const url = getUrlObject({
    baseUrl: getFrameworkServiceUrl(TENANCY_SERVICE_URL_KEY)!,
    path: '/tenant_service_groups',
    params: {
      hierarchy: 'true'
    },
  })?.toString()
  if (!url) {
    throw new Error('tenancy service url is not avaiable')
  }
  const cacheDuration = initTsgLoading ? FETCH_CACHING_DURATION : 1
  const promise = fetch(url, {
    method: 'GET',
    timeout: 10000, // 10s
    headers: {
      Authorization: `Bearer ${jwt}`,
    },
  }).then(async (res) => {
    if (res.ok) {
      initTsgLoading = false
      const data = await res.json()
      const items = data && (data.count >= 0 || !data.count) && Array.isArray(data.items) && data.items
      if (items) {
        setTimeout(() => {
          tsgsPromise = undefined
        }, cacheDuration)
        if (getFrameworkVar('local_tsgs_cache') && email) {
          data.email = email
          data.cached = Date.now()
          data.url = url
          sessionStorage.local_tsgs_cache = JSON.stringify(data)
        }
        return indexAndFilterTsgs(items, jwt)
      }
      else {
        tsgsPromise = undefined
        console.error('failed to fetch tenants tree', data, res)
        if (initTsgLoading && email && getFrameworkVar('local_tsgs_cache')) {
          const tsgs = loadTsgsCache(email, url, jwt)
          if (tsgs) {
            console.info('use cached tenants tree due to failed fetch', tsgs)
            return tsgs
          }
        }
        return indexTsgTree.fromError(data)
      }
    }
    const data = await res.text().then(tryParseErrorJson)
    tsgsPromise = undefined
    console.error('failed to fetch tenants tree', data, res)
    return indexTsgTree.fromError(data)
  }).catch((error: Error) => {
    tsgsPromise = undefined
    console.error('failed to fetch tenants tree', error)
    return indexTsgTree.fromError(error)
  })
  tsgsPromise = promise

  if (initTsgLoading && email && getFrameworkVar('local_tsgs_cache')) {
    const cached = loadTsgsCache(email, url, jwt)
    if (cached) {
      return new Promise((resolve) => {
        const ts = setTimeout(() => {
          resolve(cached)
        }, __isDebugOn() ? 1000 : FETCH_CACHING_DURATION)
        promise.then((res) => {
          clearTimeout(ts)
          resolve(res)
        })
      })
    }
  }

  return tsgsPromise
}

export const getTsgInfo = async (tsg_id: string | undefined): Promise<Tsg> => {
  if (!tsg_id) {
    throw new Error('tsg_id is required')
  }
  if (tsg_id === TSG_ID_NONE) {
    throw new Error('Cannot get tenant info for TSG ID none')
  }
  const url = getUrlObject({
    baseUrl: getFrameworkServiceUrl(TENANCY_SERVICE_URL_KEY)!,
    path: `/tenant_service_groups/${tsg_id}`,
  })?.toString()
  if (!url) {
    throw new Error('tenancy service url is not avaiable')
  }
  const promise = fetch(url, {
    method: 'GET',
    timeout: 10000, // 10s
    headers: {
      Authorization: `Bearer ${await getTsgToken(tsg_id)}`,
    },
  }).then(async (res) => {
    if (res.ok) {
      initTsgLoading = false
      const data = await res.json()
      if (data?.id && data.display_name !== undefined) {
        return data as Tsg
      }
      else {
        console.error('failed to fetch single tenant info', data, res)
        const error = new Error('Failed to get tenant information.')
        error.cause = data
        throw error
      }
    }
    const data = await res.text().then(tryParseErrorJson)
    console.error('failed to fetch single tenant info', data, res)
    const error = new Error('Failed to get tenant information.')
    error.cause = data
    throw error
  }).catch((error: Error) => {
    console.error('failed to fetch single tenant info', error)
    const err = new Error('Failed to get tenant information.')
    err.cause = error
    throw err
  })
  return promise
}

// for gcs case, as there is no tree for user
export const fetchSingleTsg = (tsg_id: string | undefined): Promise<ReadonlyTsgTree> => {
  if (tsg_id === TSG_ID_NONE) {
    return Promise.resolve(indexTsgTree([]))
  }
  return getTsgInfo(tsg_id).then((tsg) => {
    return indexTsgTree([tsg])
  }, (error) => {
    return indexTsgTree.fromError(error)
  })
}

export const freezeTsgProperties = (props: TsgProperties, noneProps?: TsgProperties): ReadonlyTsgProperties => {
  const frozenNoneProps = noneProps && Object.isFrozen(noneProps) ? noneProps :
    deepFreeze(Object.defineProperties(noneProps || {}, {
      tsg_id: { value: TSG_ID_NONE },
      has: { value: (key: string) => frozenNoneProps.hasOwnProperty(key) },
      get: { value: (key: string) => frozenNoneProps[key]?.value },
      getAsync: { value: async (key: string) => frozenNoneProps[key]?.value },
    })) as Readonly<TsgProperties>
  const { tsg_id, error, ...merged } = { ...noneProps, ...props }
  const frozen = Object.freeze(Object.defineProperties(merged, {
    tsg_id: { value: props.tsg_id },
    none: { value: frozenNoneProps },
    has: { value: (key: string) => frozen.hasOwnProperty(key) },
    get: { value: (key: string) => frozen[key]?.value },
    getAsync: { value: async (key: string) => frozen[key]?.value },
  })) as ReadonlyTsgProperties
  // deep freeze except for tsg.instances
  for (const key of Object.keys(frozen)) {
    if (key !== PROP_TSG_INSTANCES && !Object.isFrozen(frozen[key]) &&
      ((typeof frozen[key] === 'object' || typeof frozen[key] === 'function'))) {
      deepFreeze(frozen[key])
    }
  }
  return frozen
}
const TSG_ID = 'tsg_id'
export const fetchTsgProperties = (tsg_id: string): Promise<TsgProperties> => {
  const fetchPromiseFn = async (): Promise<TsgProperties> => {
    const baseUrl = getFrameworkServiceUrl(API_GW_GLOBAL_SVC_URL_KEY)!
    const url = new URL(tsg_id === TSG_ID_NONE ? '/mfe/properties/none' : '/mfe/properties', baseUrl)
    const headers = await getAuthHeaders(tsg_id)
    const doFetch = (): Promise<TsgProperties> => fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      timeout: 10000, // 10s
      headers,
    }).then(async (res) => {
      if (res.ok) {
        const data = await res.json()
        if (data?.tsg_id === tsg_id && data.count >= 0) {
          const { tsg_id, count, updated, generated, ...props } = data
          const blockNetSecFeatures = getFrameworkVar('block_netsec_features_props')
          for (const key of Object.keys(props)) {
            const prop = props[key]
            if (isPlainObject(prop)) {
              Object.defineProperty(prop, TSG_ID, { value: tsg_id })
            }
            if (blockNetSecFeatures && key.startsWith(PROP_NETSEC_FEATURES)) {
              props[`[blocked]:${key}`] = props[key]
              delete props[key]
            }
          }
          Object.defineProperty(props, TSG_ID, { value: tsg_id })
          return props as TsgProperties
        }
        const msg = 'failed to fetch properites for tsg, invalid response data'
        __error(msg, data)
        throw new Error(msg)
      }
      else if (url.pathname.endsWith('/none') && [404, 403, 401].includes(res.status)) {
        url.pathname = '/mfe/properties'
        return doFetch()
      }
      const data = await res.text().then(tryParseErrorJson, () => '')
      const msg = `failed to fetch properites for tsg: ${isString(data) ? data : data?._error?.message || res.status}`
      throw new Error(msg)
    })
    return doFetch().catch((error: Error) => {
      console.error('failed to fetch properites for tsg', error)
      const props = Object.defineProperties({}, {
        [TSG_ID]: { value: tsg_id },
        error: { value: error },
      }) as TsgProperties
      return props
    })
  }
  return dedupPromise<TsgProperties>(`auth.properties:${tsg_id}`, fetchPromiseFn, { keepPromiseIn: FETCH_CACHING_DURATION })
}

export const fetchTsgInstances = (tsg_id: string): Promise<ReadonlyTsgInstances> => {
  if (tsg_id === TSG_ID_NONE) {
    return Promise.resolve(indexTsgInstances([], TSG_ID_NONE))
  }

  const fetchPromiseFn = async (): Promise<ReadonlyTsgInstances> => {
    const baseUrl = getFrameworkServiceUrl(INSTANCE_STORE_URL_KEY)!
    const url = getUrlObject({
      baseUrl,
      path: '/instances',
      params: baseUrl?.includes('subscription') ? undefined : {
        tsg_id,
      },
    })
    if (!url) {
      const error = new Error('instance store url is not avaiable')
      Object.defineProperty(error, 'instances', { value: indexTsgInstances.fromError(error, tsg_id) })
      throw error
    }
    return fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      timeout: 10000, // 10s
      headers: await getAuthHeaders(tsg_id),
    }).then(async (res) => {
      if (res.ok) {
        const data = (await res.json()) as Instance[] | object
        if (Array.isArray(data)) {
          const insts = overrideTsgInstances(data as Instance[], tsg_id)
          return indexTsgInstances(insts, tsg_id)
        }
      }
      const data = await res.text().then(tryParseErrorJson)
      const msg = `failed to fetch instances for tsg: ${data?._error?.message || res.status}`
      const error = Object.assign(new Error(msg), data as object) as TsgInstanceError
      if (data?._error?.code === '403' && data?._error?.message === 'API Access is IP restricted.') {
        error.ipRestricted = true
      }
      return indexTsgInstances.fromError(error, tsg_id)
    }, (error: Error) => {
      console.error('failed to fetch instances for tsg', error)
      return indexTsgInstances.fromError(error, tsg_id)
    })
  }

  return dedupPromise<ReadonlyTsgInstances>(`auth.instances:${tsg_id}`, fetchPromiseFn, { keepPromiseIn: FETCH_CACHING_DURATION })
}

// will be overridden when export and fill in definition automatically
export const getTsgAccess = async (tsg_id: string, rolePermissionDefinition?: IamRolePermissionDefinition): Promise<ReadonlyAccess | undefined> => {
  if (!tsg_id) {
    throw new Error('tsg_id is required to get IAM token access')
  }
  const jwt = await getTsgToken(tsg_id)
  const access = jwt && jwtDecodeCached(jwt, tsg_id)?.access
  const parsed = access ? parseAccess(access, rolePermissionDefinition) : undefined
  return parsed
}

export const fetchAppManifests = async (jwt: string | undefined): Promise<AppManifests> => {
  const baseUrl = getFrameworkServiceUrl(APP_REGISTRY_URL_KEY)
  if (!baseUrl) {
    throw new Error('app registry url is not avaiable')
  }
  const appsUrl = getUrlObject({
    baseUrl,
    path: '/apps',
  })!
  const fetchApps = async () => fetch(appsUrl.toString(), {
    method: 'GET',
    timeout: 10000, // 10s
    headers: {
      Authorization: `Bearer ${jwt || await getTsgToken(TSG_ID_NONE)}`,
    }
  }).then(async (res) => {
    if (res.ok) {
      const data = await res.json()
      if (Array.isArray(data)) {
        return indexAppManifests(data)
      }
    }
    const data = await res.text().then(tryParseErrorJson)
    console.error('failed to fetch app manifests', data)
    return indexAppManifests.fromError(new Error('failed to fetch app manifests'))
  }, (error: Error) => {
    console.error('failed to fetch app manifests', error)
    return indexAppManifests.fromError(error)
  })

  return dedupPromise<AppManifests>('apps_manifests', fetchApps, { keepPromiseIn: 10000 }) // 10s
}

export { parseAccess, fetchIamRolePermissionDefinition }
export const fetchIamCustomRoleDefinition = async (tsg_id: string | undefined): Promise<IamCustomRolesDefinition> => {
  if (!tsg_id) {
    throw new Error('tsg_id is required to get IAM custom roles')
  }
  if (tsg_id === TSG_ID_NONE) {
    return Object.assign(indexCustomRoles([]), { tsg_id })
  }
  const customRoles = await fetchIamCustomRoleDefinitionByJwt(await getTsgToken(tsg_id))
  return Object.assign(customRoles, { tsg_id })
}

export const fetchIamScopeDefinition = async (tsg_id: string | undefined): Promise<IamScopesDefinition> => {
  if (!tsg_id) {
    throw new Error('tsg_id is required to get IAM Scope')
  }
  if (tsg_id === TSG_ID_NONE) {
    return Object.assign(indexScopes([]), { tsg_id })
  }
  const scopes = await fetchIamScopeDefinitionByJwt(await getTsgToken(tsg_id))
  return Object.assign(scopes, { tsg_id })
}


export const setDatadogGlobalContext = (keyValues: Record<string, string>): void => {
  if (!keyValues || isEmptyObject(keyValues)) {
    return
  }
  const ddRum = getDatadogRum()
  if (!ddRum?.setGlobalContextProperty) {
    return
  }
  try {
    for (const key of Object.keys(keyValues)) {
      ddRum.setGlobalContextProperty(key, keyValues[key])
    }
  }
  catch (e) {
    __error('failed to set datadog context global_tsg_id', e)
  }
}

export const fetchFeatureFlags = (tsg_id: string): Promise<ReadonlyFeatureFlags> => {
  const fetchPromiseFn = async (): Promise<ReadonlyFeatureFlags> => {
    const baseUrl = getFrameworkServiceUrl(API_GW_GLOBAL_SVC_URL_KEY)!
    const url = new URL('/ffs/features', baseUrl)
    return fetch(url.toString(), {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      timeout: 10000, // 10s
      headers: await getAuthHeaders(tsg_id),
    }).then(async (resp) => {
      if (resp.ok) {
        const features = await resp.json()
        return indexFeatureFlags(features)
      }
      const data = await resp.text().then(tryParseErrorJson)
      const err = new Error(`${resp.status}: failed to get feature flags for tsg ${tsg_id}`)
      Object.defineProperty(err, 'cause', { value: data })
      __error('failed to fetch feature flags for tsg', err)
      return indexFeatureFlags.fromError(err)
    }, (err) => {
      __error('failed to fetch feature flags for tsg', err)
      return indexFeatureFlags.fromError(err)
    })
  }
  return dedupPromise<ReadonlyFeatureFlags>(`auth.feature_flags:${tsg_id}`, fetchPromiseFn, { keepPromiseIn: FETCH_CACHING_DURATION })
}
